import { createSelector } from 'reselect';
import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { Layer, Source } from 'react-mapbox-gl';
import queryString from 'query-string';
import { useSelector } from 'react-redux';
import { getPrintableMapbox } from '../../../maps/components/selectors';
import { FilterPills } from '../../../maps';
import { QUARTER_CONTRAST, ERROR, CONTRAST } from '../../../theme';
import { getQueryString } from '../../../api';

const Container = styled.div`
  padding-bottom: 8px;
  width: ${({ halfWidth }) => (halfWidth ? '50%' : '100%')};
  display: inline-block;
  padding-right: ${({ halfWidth }) => (halfWidth ? '2px' : '0')};
  box-sizing: border-box;
`;

const Title = styled.div`
  padding-bottom: 4px;
  font-weight: bold;
  font-size: 16px;
  padding-top: 16px;
`;

const MapWrapper = styled.div`
  width: 100%;
  height: 450px;
`;

const Row = styled.div`
  flex-direction: row;
  display: flex;
  flex-wrap: wrap;
  padding-bottom: 12px;
  border-bottom: 1px solid ${QUARTER_CONTRAST};
  align-items: center;
`;

const containerStyle = {
  height: '100%',
  width: '100%',
};

const mapState = createSelector(
  ({ serverConfig }) => serverConfig,
  ({ filters }) => filters,
  ({ mapHoverFilters }) => mapHoverFilters,
  getQueryString,
  (serverConfig, filters, mapHoverFilters, querystring) => ({
    filters,
    mapHoverFilters,
    tileEndpoint: `${serverConfig.baseUrl}/tiles/${serverConfig.namespace}/{z}/{x}/{y}`,
    querystring,
  }),
);

/* eslint-disable react/style-prop-object */
export const ReportMap = ({
  identifier,
  source,
  parcels = [],
  bbox,
  padding = 12,
  radius = 500,
  filterString,
  style = {},
  layer = {},
  halfWidth = false,
  showRadius = false,
  singleParcel = false,
  properties = {},
}) => {
  const { tileEndpoint, filters, mapHoverFilters, querystring } = useSelector(mapState);
  const [zoom, setZoom] = useState(16);
  const { mapStyle } = properties;

  const Mapbox = useSelector(getPrintableMapbox);

  const tileSourceOptions = {
    type: 'vector',
    tiles: [tileEndpoint],
  };

  const tileSource = { ...tileSourceOptions };
  const url = tileSource.tiles[0];

  const queryObj = {
    ...mapHoverFilters,
    [identifier]: parcels,
    props: identifier,
  };
  if (source) queryObj.source = source;
  const query = queryString.stringify(queryObj);

  const forceDots = !singleParcel && (layer.dataKey === 'opp_id' || layer.dataKey === 'parcel_id' || !layer.dataKey);
  const layerStyleType = layer.style ? layer.style.type : 'line';
  const styleType = (forceDots && zoom < 12.8) ? 'circle' : layerStyleType;

  tileSource.tiles = [
    `${url
    }?${query
    }${filterString ? `&${filterString}` : ''
    }${querystring ? `&${querystring}` : ''
    }${styleType === 'circle'
      ? '&geom=geom_pt'
      : ''
    }`,
  ];

  const sourceId = `source-${styleType}`;

  const paint = {
    [`${styleType}-color`]: layer.style ? layer.style.paint[`${layer.style.type}-color`] : ERROR,
    [`${styleType}-opacity`]: 1,
  };

  if (styleType === 'circle') {
    paint[`${styleType}-radius`] = 4;
  }

  if (styleType === 'line') {
    paint[`${styleType}-width`] = 4;
  }

  const layerEl = (
    <div key={sourceId}>
      <Source id={sourceId} tileJsonSource={tileSource} />
      <Layer
        type={styleType || 'line'}
        id={`layer-${sourceId}`}
        sourceLayer="default"
        sourceId={sourceId}
        paint={paint}
      />
    </div>
  );

  let radiusLayerEl = null;

  if (showRadius) {
    const radiusTileSoruce = { ...tileSource };
    radiusTileSoruce.tiles = [
      `${url}?${query}${filterString ? `&${filterString}` : ''
      }&radius=radius%3A${radius}`,
    ];
    const radiusPaint = style.paint || {
      'line-color': CONTRAST,
      'line-width': 1,
    };
    radiusLayerEl = (
      <div>
        <Source id={`${sourceId}-radius`} tileJsonSource={radiusTileSoruce} />
        <Layer
          type={styleType || 'line'}
          id="radius-layer-id"
          sourceLayer="default"
          sourceId={`${sourceId}-radius`}
          paint={radiusPaint}
        />
      </div>
    );
  }

  const onMoveEnd = useCallback((map) => setZoom(map.getZoom()), [setZoom]);

  return (
    <Container halfWidth={halfWidth}>
      <MapWrapper>
        <Mapbox
          preserveDrawingBuffer
          fitBounds={bbox}
          fitBoundsOptions={{
            padding,
          }}
          onZoomEnd={onMoveEnd}
          containerStyle={containerStyle}
          style={`mapbox://styles/mapbox/${mapStyle || 'light-v9'}`}
        >
          {radiusLayerEl}
          {layerEl}
        </Mapbox>
      </MapWrapper>
      {layer.style
        ? [
          <Title key="title">Filters</Title>,
          <Row key="row">
            <FilterPills
              layer={layer}
              horizontal
              filterDefinitions={filters}
            />
          </Row>,
        ]
        : null}
    </Container>
  );
};
